import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { GridCellParams } from '@mui/x-data-grid-pro';
import { Link, Box, Badge } from '@mui/material';

import { translate as t } from 'hooks/useTranslations';
import { AccreditingBodiesContext } from '../context';
import { ActionMenu, QuickSearch } from 'components/_atoms';
import datetimeHelper from 'helpers/datetimeHelper';
import useAccreditationBodies from '../../_data_sources/AccreditationBodies/useAccreditationBodies';
import { AccreditationBody } from '../../_types/awb';
import useUrlParams from 'hooks/useUrlParams';
import { ActionMenuItemProps } from 'components/_atoms/ActionMenu';
import NewAccreditingBody from '../New';
import EditAccreditingBody from '../Edit';
import CommonLayout from 'views/App/Portal/_layouts/CommonLayout';
import PageToolbar from '_lex/_atoms/PageToolbar';
import GridSection, { ColumnDef } from 'components/_layouts/GridSection';
import '../../utils/cleanBodies';

type AccreditingBodiesListParams = {
  search?: string | null;
};

const defaultParams: AccreditingBodiesListParams = {
  search: null,
};

const AccreditingBodiesList: React.FC = () => {
  const {} = useContext(AccreditingBodiesContext);
  const [params, setParams] = useUrlParams(defaultParams);
  const [accreditingBodies, setAccreditingBodies] = useState<AccreditationBody[]>([]);
  const [lastId, setLastId] = useState<string | null>(null);
  const [editId, setEditId] = useState<string | null>(null);

  const updateParams = useCallback(
    (updatedParams: AccreditingBodiesListParams) => {
      setParams((prev: any) => ({
        ...prev,
        ...updatedParams,
      }));
      document.body.dispatchEvent(new Event('params-changed'));
    },
    [setParams]
  );

  const queryParams = useMemo(() => {
    const newParams = { ...params };
    return newParams;
  }, [params]);

  const gridViewQuery = useAccreditationBodies<AccreditationBody>(queryParams, {}, (errors) => {
    console.error('Error fetching accrediting bodies:', errors);
  });

  useEffect(() => {
    if (gridViewQuery.data && !gridViewQuery.isError) {
      const fetchedAccreditingBodies = gridViewQuery.data.pages.flatMap((page) => page.content) || [];
      setAccreditingBodies(fetchedAccreditingBodies);
    }
  }, [gridViewQuery.data, gridViewQuery.isError]);

  const columns: ColumnDef = useMemo(
    () => ({
      abbreviation: {
        header: t('awb.accreditingBodies.properties.abbreviation'),
        flex: 0.4,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return (
            <Link
              id={`accrediting_body_${row.id}`}
              href={`./${row.id}/versions`}
            >
              {row.abbreviation}
            </Link>
          );
        },
      },
      name: {
        header: t('awb.accreditingBodies.properties.name'),
        flex: 2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{row.name}</span>;
        },
      },
      createdAt: {
        header: t('awb.accreditingBodies.properties.createdAt'),
        flex: 0.5,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{datetimeHelper.formatDate(row.createdAt)}</span>;
        },
      },
      updatedAt: {
        header: t('awb.accreditingBodies.properties.updatedAt'),
        flex: 0.5,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          return <span>{datetimeHelper.formatDate(row.updatedAt)}</span>;
        },
      },
      actions: {
        header: '',
        type: 'actions',
        flex: 0.2,
        renderer: (params: GridCellParams) => {
          const row = params.row;
          if (!row) return null;
          const items: ActionMenuItemProps[] = [
            {
              key: `open_${row.id}`,
              label: t('awb.accreditingBodies.actions.open'),
              onClick: () => {},
            },
            {
              key: `edit_${row.id}`,
              label: t('awb.accreditingBodies.actions.edit'),
              onClick: () => setEditId(row.id),
            },
          ];
          return (
            <ActionMenu
              id={`action_menu_${row.id}`}
              items={items}
            />
          );
        },
      },
    }),
    [t]
  );

  const actions = useMemo(() => <NewAccreditingBody setLastId={setLastId} />, []);

  useEffect(() => {
    if (lastId && accreditingBodies && document.getElementById(`accrediting_body_${lastId}`)) {
      setLastId(null);
      document.getElementById(`accrediting_body_${lastId}`)?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [lastId, accreditingBodies]);

  const gridViewQueryData = useMemo(
    () => ({
      hasNextPage: gridViewQuery.hasNextPage,
      isFetchingNextPage: gridViewQuery.isFetchingNextPage,
      fetchNextPage: gridViewQuery.fetchNextPage,
      isError: gridViewQuery.isError,
      isLoading: gridViewQuery.isLoading,
      error: gridViewQuery.error,
      data: {
        pages:
          gridViewQuery.data?.pages.map((page) => ({
            count: page.count,
            rows: page.content,
          })) || [],
      },
    }),
    [gridViewQuery]
  );

  return (
    <CommonLayout
      headerProps={{
        title: t('awb.accreditingBodies.title'),
        subtitle: t('awb.accreditingBodies.subtitle'),
        titleBadge:
          (gridViewQuery.data?.pages[0]?.count ?? 0) > 0 ? (
            <Badge
              variant='inline'
              badgeContent={gridViewQuery.data?.pages[0]?.count}
              color='neutral'
              sx={{ textTransform: 'uppercase' }}
            />
          ) : undefined,
        actions: actions,
      }}
    >
      <Box sx={{ display: 'grid', gridTemplateRows: 'auto 1fr', gap: 4, minHeight: 0 }}>
        <PageToolbar
          leftItems={
            <QuickSearch
              id='keyword_search'
              initialValue={params.search || ''}
              onChange={(val) => updateParams({ search: val })}
              placeholder={t('awb.accreditingBodies.search.placeholder')}
            />
          }
        />
        <GridSection
          columns={columns}
          data={accreditingBodies}
          query={gridViewQueryData}
        />
      </Box>

      <EditAccreditingBody
        id={editId || ''}
        setLastId={setLastId}
        setEditId={setEditId}
      />
    </CommonLayout>
  );
};

export default AccreditingBodiesList;
